import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Dropdown } from "../Dropdown";
import Input from "../Input";
import TrashButtonIcon from "../../assets/Trash_Btn.svg";
import styles from "./PlaylistInfoForm.module.css";

export function PlaylistInfoForm({
  title = "",
  location = "",
  locationOptions = [],
  type = "",
  typeLabel = "Type",
  typeOptions = [],
  enableLocationDropdown = false,
  onChange = () => {},
  onAddType = () => {},
  onDeleteType = () => {},
  titleError = false,
  className,
}) {
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [isAddingType, setIsAddingType] = useState(false);
  const [newTypeLabel, setNewTypeLabel] = useState("");
  const selectedLocation = location || locationOptions[0] || "";
  const uniqueTypeOptions = typeOptions.reduce((accumulator, option) => {
    const normalizedLabel = String(option.label || "")
      .trim()
      .toLowerCase();

    if (!normalizedLabel) {
      return accumulator;
    }

    if (
      accumulator.some(
        (item) =>
          String(item.label || "")
            .trim()
            .toLowerCase() === normalizedLabel,
      )
    ) {
      return accumulator;
    }

    accumulator.push(option);
    return accumulator;
  }, []);

  const typeOptionByLabel = uniqueTypeOptions.reduce((accumulator, option) => {
    accumulator[
      String(option.label || "")
        .trim()
        .toLowerCase()
    ] = option;
    return accumulator;
  }, {});

  useEffect(() => {
    if (!isTypeOpen) {
      setIsAddingType(false);
      setNewTypeLabel("");
    }
  }, [isTypeOpen]);

  const handleTitleChange = (e) => {
    onChange({
      title: e.target.value,
      location: selectedLocation,
      type,
    });
  };

  const handleLocationChange = (newLocation) => {
    onChange({
      title,
      location: newLocation,
      type,
    });
  };

  const handleTypeChange = (newType) => {
    // Don't allow selecting "Add" as a type
    if (newType !== "Add") {
      onChange({
        title,
        location,
        type: newType,
      });
    }
  };

  const handleAddTypeSave = () => {
    if (newTypeLabel.trim()) {
      onAddType(newTypeLabel.trim());
      setNewTypeLabel("");
      setIsAddingType(false);
      setIsTypeOpen(false);
    }
  };

  const handleAddTypeCancel = () => {
    setNewTypeLabel("");
    setIsAddingType(false);
  };

  const handleDeleteType = (typeLabel) => {
    onDeleteType(typeLabel);
  };

  const handleDeleteTypeFromMenu = (event, typeLabel) => {
    event.stopPropagation();
    handleDeleteType(typeLabel);
    setIsTypeOpen(false);
  };

  const renderTypeMenu = ({ visibleOptions }) => (
    <>
      <div className={styles.optionsList}>
        {visibleOptions.map((optionLabel) => {
          const normalizedLabel = String(optionLabel || "")
            .trim()
            .toLowerCase();
          const optionMeta = typeOptionByLabel[normalizedLabel];

          return (
            <div key={optionLabel} className={styles.optionRow}>
              <button
                type="button"
                className={styles.optionButton}
                onClick={() => {
                  handleTypeChange(optionLabel);
                  setIsTypeOpen(false);
                }}
              >
                {optionLabel}
              </button>
              {optionMeta?.isUserAdded ? (
                <button
                  type="button"
                  className={styles.deleteButton}
                  onClick={(event) =>
                    handleDeleteTypeFromMenu(event, optionMeta.label)
                  }
                  aria-label={`Delete ${optionMeta.label}`}
                >
                  <img
                    src={TrashButtonIcon}
                    alt=""
                    className={styles.trashIcon}
                  />
                </button>
              ) : null}
            </div>
          );
        })}
      </div>

      {isAddingType ? (
        <div className={styles.addRow}>
          <input
            type="text"
            placeholder="Type name"
            value={newTypeLabel}
            onChange={(e) => setNewTypeLabel(e.target.value)}
            className={styles.addInput}
            autoFocus
          />
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleAddTypeSave}
            disabled={!newTypeLabel.trim()}
          >
            Save
          </button>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={handleAddTypeCancel}
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className={styles.addLink}
          onClick={() => setIsAddingType(true)}
        >
          Add
        </button>
      )}
    </>
  );

  return (
    <section className={classNames(styles.container, className)}>
      <h3 className={styles.heading}>Playlist Information</h3>
      <div className={styles.divider} />

      <div className={styles.formFields}>
        {/* Title Field */}
        <div className={styles.formGroup}>
          <label htmlFor="playlist-title" className={styles.label}>
            Title
            <span className={styles.required}> *</span>
          </label>
          <Input
            id="playlist-title"
            type="text"
            placeholder=""
            value={title}
            onChange={handleTitleChange}
            className={classNames(styles.titleInput, {
              [styles.titleInputError]: titleError,
            })}
          />
          {titleError && (
            <div className={styles.errorMessage}>
              This field is required.
            </div>
          )}
        </div>

        {/* Location Field */}
        <div className={styles.inlineRowLocation}>
          <label
            htmlFor="playlist-location"
            className={styles.inlineLabelLocation}
          >
            Playlist Location
          </label>
          <Dropdown
            value={selectedLocation}
            options={locationOptions}
            placeholder="Select Location"
            onChange={handleLocationChange}
            isOpen={enableLocationDropdown ? undefined : false}
            onOpenChange={enableLocationDropdown ? undefined : () => {}}
            className={styles.locationDropdownRoot}
            buttonClassName={styles.locationDropdownButton}
            buttonOpenClassName={styles.locationDropdownButtonOpen}
            valueClassName={styles.locationDropdownValue}
            chevronClassName={styles.locationChevron}
            chevronOpenClassName={styles.locationChevronUp}
            menuClassName={styles.locationDropdownMenu}
            optionClassName={styles.locationDropdownOption}
          />
        </div>

        {/* Type Field */}
        <div className={styles.inlineRowType}>
          <label htmlFor="playlist-type" className={styles.inlineLabelType}>
            {typeLabel}
          </label>
          <div className={styles.typeDropdownWrapper}>
            <Dropdown
              value={type}
              options={typeOptions.map((opt) => opt.label)}
              placeholder="Select Type"
              isOpen={isTypeOpen}
              onOpenChange={setIsTypeOpen}
              renderMenu={renderTypeMenu}
              className={styles.dropdownArea}
              buttonClassName={styles.dropdownButton}
              buttonOpenClassName={styles.dropdownButtonOpen}
              valueClassName={styles.dropdownValue}
              chevronClassName={styles.chevron}
              chevronOpenClassName={styles.chevronUp}
              menuClassName={styles.dropdownMenu}
            />
          </div>
        </div>
      </div>
    </section>
  );
}

PlaylistInfoForm.propTypes = {
  title: PropTypes.string,
  location: PropTypes.string,
  locationOptions: PropTypes.arrayOf(PropTypes.string),
  type: PropTypes.string,
  typeLabel: PropTypes.string,
  typeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isUserAdded: PropTypes.bool,
    }),
  ),
  enableLocationDropdown: PropTypes.bool,
  onChange: PropTypes.func,
  onAddType: PropTypes.func,
  onDeleteType: PropTypes.func,
  titleError: PropTypes.bool,
  className: PropTypes.string,
};

export default PlaylistInfoForm;
